import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePolicyAdmin } from '../hooks/usePolicyAdmin';
import { useQuickRefer } from '../hooks/useQuickRefer';
import Header from '../components/Header';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import { accelerantService } from '../api/accelerant/AccelerantService';
import { ApplicationDocumentType } from '../api/policyAdmin/types/PolicyAdmin.types';
import { ApiCallType, ApiHistoryEntry, ApplicationHistory, QuickReferPayload, QuickReferralForm } from '../api/quickrefer/types/QuickRefer.types';
import '../styles/ApplicationForm.css';

const ApplicationForm: React.FC = () => {
  const { policySystem, id } = useParams<{ policySystem: string; id: string }>();
  const { application, documents, loading, error, getApplication, getDocuments, clearErrors } = usePolicyAdmin();
  const { getReferralById, referral, saveReferral } = useQuickRefer();
  const [initialLoading, setInitialLoading] = useState(true);
  const [formData, setFormData] = useState<QuickReferralForm>({
    summaryOfRisk: '',
    referralReasons: [],
    documents: []
  });
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [duplicateDocumentNames, setDuplicateDocumentNames] = useState<string[]>([]);
  const [currentSessionHistory, setCurrentSessionHistory] = useState<ApiHistoryEntry[]>([]);
  const [validationErrors, setValidationErrors] = useState<{
    referralReasons?: boolean;
    summaryOfRisk?: boolean;
  }>({});
  const referralOptions = ['Hazard Exposure', 'Limits', 'Claims Experience'];

  const createHistoryEntry = <T, E>(
    apiCallType: ApiCallType,
    apiUrl: string,
    result: { isOk: boolean; isErr: boolean; value?: T; error?: E }
  ): ApiHistoryEntry => {
    if (result.isErr && result.error) {
      return {
        type: apiCallType,
        success: false,
        error: result.error instanceof Error ? result.error.message : String(result.error),
        url: apiUrl,
        timestamp: new Date().toISOString()
      };
    } else {
      return {
        type: apiCallType,
        success: true,
        url: apiUrl,
        response: result.value,
        timestamp: new Date().toISOString()
      };
    }
  };

  const saveSubmissionWithHistory = async (
    data: {status: string, accelerantReferralId: string | null, accelerantStatus: string},
    historyEntries: ApiHistoryEntry[]
  ) => {
    if (!policySystem || !id) return;

    const existingHistories = referral?.history || [];
    
    const hasFailed = historyEntries.some(entry => !entry.success);
    const failedStep = historyEntries.find(entry => !entry.success);
    const lastSuccessfulEntry = [...historyEntries].reverse().find(entry => entry.success);
    
    const newHistory: ApplicationHistory = {
      submittedAt: new Date().toISOString(),
      entries: historyEntries,
      status: hasFailed ? 'failed' : 'success',
      failedAt: failedStep?.type,
      lastSuccessfulStep: lastSuccessfulEntry?.type
    };
    
    const updatedHistories = [...existingHistories, newHistory];

    const payload: QuickReferPayload = {
      pas: policySystem,
      pas_application_id: id,
      status: data.status,
      accelerant_referral_id: data.accelerantReferralId,
      accelerant_status: data.accelerantStatus || null,
      history: updatedHistories,
      ...(data.accelerantReferralId && { data: formData })
    };

    const result = await saveReferral(payload);
    
    if (result.isErr) {
      setSubmitError(`Failed to save submission: ${result.error.message}`);
      return undefined;
    }
    
    return result.value;
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      if (policySystem && id) {
        setInitialLoading(true);
        const initialHistoryEntries: ApiHistoryEntry[] = [];
        
        const referralResult = await getReferralById(policySystem, id);
        
        if (referralResult && referralResult.status === 'submitted') {
          setInitialLoading(false);
          return;
        }

        const applicationResult = await getApplication(policySystem, id);
        initialHistoryEntries.push(
          createHistoryEntry(
            ApiCallType.GET_APPLICATION_BY_ID,
            `/api/policy-admin/${policySystem}/applications/${id}`,
            applicationResult
          )
        );

        const documentsResult = await getDocuments(policySystem, id);
        initialHistoryEntries.push(
          createHistoryEntry(
            ApiCallType.GET_DOCUMENTS,
            `/api/policy-admin/${policySystem}/applications/${id}/documents`,
            documentsResult
          )
        );
        
        setCurrentSessionHistory(initialHistoryEntries);
        setInitialLoading(false);
      }
    };

    fetchInitialData();
  }, [policySystem, id, getApplication, getDocuments, getReferralById]);


  useEffect(() => {
    if (application) {
      setFormData(prev => ({
        ...prev,
        type: application.type,
        named_insured: application.named_insured,
        number: application.number,
        program: application.program,
        underwriter: application.underwriter,
        effective_date: application.effective_date,
        expiration_date: application.expiration_date,
        total_locations_limit: application.total_locations_limit,
        summaryOfRisk: application.internal_underwriting_notes || prev.summaryOfRisk
      }));
    }
  }, [application]);

  useEffect(() => {
    if (documents.length > 0) {
      setFormData(prev => ({
        ...prev,
        documents: documents
      }));
      
      const names = documents.map(doc => doc.name);
      const duplicates: string[] = [];
      const seen = new Set<string>();
      
      names.forEach(name => {
        if (name && seen.has(name)) {
          if (!duplicates.includes(name)) {
            duplicates.push(name);
          }
        }
        if (name) {
          seen.add(name);
        }
      });
      
      setDuplicateDocumentNames(duplicates);
    }
  }, [documents]);

  useEffect(() => {
    if (referral && referral.data) {
      setFormData(referral.data);
    }
  }, [referral]);

  const handleRetry = async () => {
    if (policySystem && id) {
      clearErrors();
      const retryHistoryEntries: ApiHistoryEntry[] = [];

      const applicationResult = await getApplication(policySystem, id);
      retryHistoryEntries.push(
        createHistoryEntry(
          ApiCallType.GET_APPLICATION_BY_ID,
          `/api/policy-admin/${policySystem}/applications/${id}`,
          applicationResult
        )
      );

      const documentsResult = await getDocuments(policySystem, id);
      retryHistoryEntries.push(
        createHistoryEntry(
          ApiCallType.GET_DOCUMENTS,
          `/api/policy-admin/${policySystem}/applications/${id}/documents`,
          documentsResult
        )
      );

      setCurrentSessionHistory(retryHistoryEntries);
    }
  };

  const handleSubmit = async () => {
    if (policySystem && id) {
      await getReferralById(policySystem, id);
    }

    if (!application || !policySystem || !id) {
      setSubmitError('Missing application data');
      return;
    }

    setValidationErrors({});
    
    let hasErrors = false;
    const errors: { referralReasons?: boolean; summaryOfRisk?: boolean } = {};
    
    if (formData.referralReasons.length === 0) {
      setSubmitError('Please select at least one referral reason');
      errors.referralReasons = true;
      hasErrors = true;
    }

    if (!formData.summaryOfRisk.trim()) {
      if (hasErrors) {
        setSubmitError('Please fill in all required fields');
      } else {
        setSubmitError('Please provide a summary of risk');
      }
      errors.summaryOfRisk = true;
      hasErrors = true;
    }
    
    if (hasErrors) {
      setValidationErrors(errors);
      return;
    }

    const hazardHubDocuments = formData.documents.filter((doc) => doc.type === ApplicationDocumentType.HAZARD_HUB);
    if (hazardHubDocuments.length === 0) {
      setSubmitError('Please generate the Hazard Hub document in the policy admin system first');
      return;
    }

    setLoadingSubmit(true);
    setSubmitError(null);
    
    const allHistoryEntries: ApiHistoryEntry[] = [...currentSessionHistory];

    const submissionData = {
      applicationId: id,
      policySystem: policySystem,
      namedInsured: formData.named_insured || '',
      quote: formData.number || '',
      program: formData.program || '',
      effectiveDate: formData.effective_date,
      expirationDate: formData.expiration_date,
      limit: formData.total_locations_limit,
      referralReasons: formData.referralReasons,
      summaryOfRisk: formData.summaryOfRisk,
      applicationData: formData,
      applicationType: formData.type,
      underwriter: formData.underwriter
    };

    let accelerantId: string | null = null;
    let shouldRunCreateDraft = true;
    let shouldRunUploadAttachments = true;
    let shouldRunUpdateStatus = true;
    let shouldRunGetReferral = true;

    if (referral && referral.status === 'failed' && referral.history && referral.history.length > 0) {
      const lastHistory = referral.history[referral.history.length - 1];
      
      if (lastHistory.failedAt) {
        switch (lastHistory.failedAt) {
          case ApiCallType.CREATE_DRAFT:
            shouldRunCreateDraft = true;
            shouldRunUploadAttachments = true;
            shouldRunUpdateStatus = true;
            shouldRunGetReferral = true;
            break;
          
          case ApiCallType.UPLOAD_ATTACHMENTS:
            shouldRunCreateDraft = false;
            shouldRunUploadAttachments = true;
            shouldRunUpdateStatus = true;
            shouldRunGetReferral = true;
            accelerantId = referral.accelerant_referral_id || null;
            break;
          
          case ApiCallType.UPDATE_STATUS_SUBMITTED:
            shouldRunCreateDraft = false;
            shouldRunUploadAttachments = false;
            shouldRunUpdateStatus = true;
            shouldRunGetReferral = true;
            accelerantId = referral.accelerant_referral_id || null;
            break;
          
          case ApiCallType.GET_REFERRAL_BY_ID:
            shouldRunCreateDraft = false;
            shouldRunUploadAttachments = false;
            shouldRunUpdateStatus = false;
            shouldRunGetReferral = true;
            accelerantId = referral.accelerant_referral_id || null;
            break;
          
          default:
            break;
        }
      }
    }

    if (shouldRunCreateDraft) {
      const submitResult = await accelerantService.submitApplication(submissionData);
      allHistoryEntries.push(
        createHistoryEntry(
          ApiCallType.CREATE_DRAFT,
          '/api/accelerant/referrals',
          submitResult
        )
      );
      
      if (submitResult.isErr) {
        setSubmitError(`Failed to create draft: ${submitResult.error?.message || 'Unknown error'}`);
        await saveSubmissionWithHistory(
          {status: 'failed', accelerantReferralId: null, accelerantStatus: 'failed'},
          allHistoryEntries
        );
        setLoadingSubmit(false);
        return;
      }
      
      if (!submitResult.value?.referralId) {
        setSubmitError('Failed to get referral ID from Accelerant response');
        await saveSubmissionWithHistory(
          {status: 'failed', accelerantReferralId: null, accelerantStatus: 'failed'},
          allHistoryEntries
        );
        setLoadingSubmit(false);
        return;
      }
      
      accelerantId = submitResult.value.referralId;
    } else if (!accelerantId) {
      setSubmitError('No referral ID available to continue submission');
      setLoadingSubmit(false);
      return;
    }

    if (shouldRunUploadAttachments) {
      const getReferralResult = await accelerantService.getReferralById(accelerantId!);
      if (getReferralResult.isErr) {
        setSubmitError(`Failed to get referral details: ${getReferralResult.error?.message || 'Unknown error'}`);
        return;
      }

      let docs = formData.documents.map((doc) => ({ url: doc.url, name: doc.name }));

      const attachmentResult = await accelerantService.uploadAttachments(
        accelerantId!, 
        docs
      );
      allHistoryEntries.push(
        createHistoryEntry(
          ApiCallType.UPLOAD_ATTACHMENTS,
          `/api/accelerant/referrals/${accelerantId}/attachments`,
          attachmentResult
        )
      );
      
      if (attachmentResult.isErr) {
        setSubmitError(`Failed to upload attachments: ${attachmentResult.error?.message || 'Unknown error'}`);
        await saveSubmissionWithHistory(
          {status: 'failed', accelerantReferralId: accelerantId!, accelerantStatus: 'failed'},
          allHistoryEntries
        );
        setLoadingSubmit(false);
        return;
      }
    }

    if (shouldRunUpdateStatus) {
      const statusUpdateResult = await accelerantService.updateReferralStatus(accelerantId!, 'submitted');
      allHistoryEntries.push(
        createHistoryEntry(
          ApiCallType.UPDATE_STATUS_SUBMITTED,
          `/api/accelerant/referrals/${accelerantId}/status`,
          statusUpdateResult
        )
      );
      
      if (statusUpdateResult.isErr) {
        setSubmitError(`Failed to update status: ${statusUpdateResult.error?.message || 'Unknown error'}`);
        await saveSubmissionWithHistory(
          {status: 'failed', accelerantReferralId: accelerantId!, accelerantStatus: 'failed'},
          allHistoryEntries
        );
        setLoadingSubmit(false);
        return;
      }
    }

    let getReferralResult: any = null;
    if (shouldRunGetReferral) {
      getReferralResult = await accelerantService.getReferralById(accelerantId!);
      allHistoryEntries.push(
        createHistoryEntry(
          ApiCallType.GET_REFERRAL_BY_ID,
          `/api/accelerant/referrals/${accelerantId}`,
          getReferralResult
        )
      );
      if (getReferralResult.isErr) {
        setSubmitError(`Failed to get referral details: ${getReferralResult.error?.message || 'Unknown error'}`);
        await saveSubmissionWithHistory(
          {status: 'failed', accelerantReferralId: accelerantId!, accelerantStatus: 'failed'},
          allHistoryEntries
        );
        setLoadingSubmit(false);
        return;
      }
    }
    
    const accelerantState = getReferralResult?.value?.state;
    if (!accelerantState) {
      setSubmitError('Failed to get state from Accelerant response');
      await saveSubmissionWithHistory(
        {status: 'failed', accelerantReferralId: accelerantId!, accelerantStatus: 'failed'},
        allHistoryEntries
      );
      setLoadingSubmit(false);
      return;
    }
    
    await saveSubmissionWithHistory(
      {status: 'submitted', accelerantReferralId: accelerantId!, accelerantStatus: accelerantState},
      allHistoryEntries
    );
    setSubmitError(null);
    setLoadingSubmit(false);
  };

  const hasSubmittedSubmission = referral && referral.status === 'submitted';

  if (initialLoading || (!hasSubmittedSubmission && !application && (loading.application || loading.documents))) {
    return (
      <>
        <Header />
        <div className="application-form">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <h2>Loading Application</h2>
            <p className="loading-text">
              Fetching application details from {policySystem ? policySystem.charAt(0).toUpperCase() + policySystem.slice(1) : 'PAS'}...
            </p>
          </div>
        </div>
      </>
    );
  }

  if (!hasSubmittedSubmission && (error.application || error.documents)) {
    return (
      <div className="application-form">
        <div className="error">
          <h2>Error Loading {error.application ? 'Application' : 'Documents'}</h2>
          <p className="error-message">{error.application || error.documents}</p>
          <button onClick={handleRetry} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="application-form">
        
        {referral && referral.status === 'submitted' && (
          <div className="success-message-container">
            <div className="success-message-content">
              <div className="success-message-left">
                <div className="success-checkmark-circle">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12L10 17L20 7" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span className="success-message-text">
                  APPLICATION SUCCESSFULLY SUBMITTED
                </span>
              </div>
              {referral.accelerant_referral_id && (
                <button
                  className="go-to-accelerant-btn"
                  onClick={() => window.open(`https://app.accelerant.ai/underwriting-referrals/${referral.accelerant_referral_id}`, '_blank')}
                >
                  Go to Accelerant
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 13V19C18 20.1046 17.1046 21 16 21H5C3.89543 21 3 20.1046 3 19V8C3 6.89543 3.89543 6 5 6H11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M15 3H21M21 3V9M21 3L10 14" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
            <p className="submission-note">This application has been submitted and the form is now read-only.</p>
          </div>
        )}
        
        {referral && referral.status === 'failed' && referral.history && referral.history.length > 0 && (
          <div className="failed-status-message">
            <div className="status-badge">
              <div className="status-warning-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 8V12M12 16H12.01" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="status-text-error">
                Previous Submission Failed
                {(() => {
                  const lastHistory = referral.history[referral.history.length - 1];
                  if (lastHistory.failedAt) {
                    const failureMessages: {[key: string]: string} = {
                      [ApiCallType.CREATE_DRAFT]: 'Creating Draft',
                      [ApiCallType.UPLOAD_ATTACHMENTS]: 'Uploading Attachments',
                      [ApiCallType.UPDATE_STATUS_SUBMITTED]: 'Updating Status',
                      [ApiCallType.GET_REFERRAL_BY_ID]: 'Getting Referral Details'
                    };
                    return ` at: ${failureMessages[lastHistory.failedAt] || lastHistory.failedAt}`;
                  }
                  return '';
                })()}
              </span>
            </div>
            <p className="submission-note-error">
              Click "Retry Submission" to retry from where it left off. 
              {referral.accelerant_referral_id && (
                <>
                  {' (If you want to go to your DRAFT referral, '}
                  <a 
                    href={`https://app.accelerant.ai/underwriting-referrals/${referral.accelerant_referral_id}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: '#0066cc', textDecoration: 'underline' }}
                  >
                    click here
                  </a>
                  {')'}
                </>
              )}
            </p>
          </div>
        )}
        
        <h1 className="form-main-title">Application Form</h1>

        <div className="form-info">
          <p><strong className="form-label-strong">Policy System:</strong> {policySystem ? policySystem.charAt(0).toUpperCase() + policySystem.slice(1) : 'PAS'}</p>
          {formData.named_insured && (
            <p><strong className="form-label-strong">Application Name:</strong> {formData.named_insured}</p>
          )}
        </div>

        <div className="form-container">
          <h3 className="form-section-title">Application Details</h3>
          <div className="application-details">
            <div className="detail-row">
              <label>Type:</label>
              <span>{formData.type || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <label>Named Insured:</label>
              <span>{formData.named_insured || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <label>Quote:</label>
              <span>{formData.number || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <label>Program:</label>
              <span>{formData.program || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <label>Underwriter:</label>
              <span>{formData.underwriter || 'N/A'}</span>
            </div>
            <div className={`detail-row ${validationErrors.referralReasons ? 'has-error' : ''}`}>
              <label>Referral Reasons:<span className="required-field">*</span></label>
              {referral?.status === 'submitted' ? (
                <span>{formData.referralReasons.length > 0 ? formData.referralReasons.join(', ') : 'N/A'}</span>
              ) : (
                <MultiSelectDropdown
                  options={referralOptions}
                  value={formData.referralReasons}
                  onChange={(reasons) => {
                    setFormData(prev => ({ ...prev, referralReasons: reasons }));
                    if (reasons.length > 0 && validationErrors.referralReasons) {
                      setValidationErrors(prev => ({ ...prev, referralReasons: false }));
                      setSubmitError(null);
                    }
                  }}
                  placeholder="Select referral reasons"
                  disabled={false}
                />
              )}
            </div>
            <div className="detail-row">
              <label>Effective Date:</label>
              <span>{formData.effective_date}</span>
            </div>
            <div className="detail-row">
              <label>Expiration Date:</label>
              <span>{formData.expiration_date}</span>
            </div>
            <div className="detail-row">
              <label>Limit:</label>
              <span>
                {formData.total_locations_limit ? `$${formData.total_locations_limit.toLocaleString()}` : 'N/A'}
              </span>
            </div>
          </div>
          <div className={`form-section ${validationErrors.summaryOfRisk ? 'has-error' : ''}`}>
            <h3 className="form-section-title">Summary of Risk<span className="required-field">*</span></h3>
            <textarea
              className={`summary-textarea ${validationErrors.summaryOfRisk ? 'error' : ''}`}
              value={formData.summaryOfRisk}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, summaryOfRisk: e.target.value }));
                if (e.target.value.trim() && validationErrors.summaryOfRisk) {
                  setValidationErrors(prev => ({ ...prev, summaryOfRisk: false }));
                  setSubmitError(null);
                }
              }}
              placeholder="Enter summary of risk..."
              rows={6}
              disabled={referral?.status === 'submitted'}
            />
          </div>

          {(formData.documents.length > 0) && (
            <div className="form-section">
              <h3 className="form-section-title">Documents</h3>
              {duplicateDocumentNames.length > 0 && (
                <div className="duplicate-warning">
                  <div className="warning-icon">⚠️</div>
                  <div className="warning-content">
                    <strong>Duplicate document names detected:</strong>
                    <ul className="duplicate-list">
                      {duplicateDocumentNames.map((name, index) => (
                        <li key={index}>{name}</li>
                      ))}
                    </ul>
                    <p className="warning-message">Please go back to {policySystem ? policySystem.charAt(0).toUpperCase() + policySystem.slice(1) : 'PAS'} and rename the duplicate documents before submitting.</p>
                  </div>
                </div>
              )}
              <div className="attachments-list">
                {formData.documents.map((doc, index) => (
                  <div 
                    key={`document-${index}`} 
                    className={`attachment-item ${duplicateDocumentNames.includes(doc.name) ? 'duplicate-document' : ''}`}
                  >
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noreferrer"
                      className="attachment-link"
                    >
                      📄 {doc.name || `Document ${index + 1}`}
                      {duplicateDocumentNames.includes(doc.name) && ' (Duplicate)'}
                    </a>
                    {doc.created && (
                      <span className="attachment-date">
                        {new Date(doc.created).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {referral?.status !== 'submitted' && (
            <div className="form-section">
              <div className="submit-section">
                <button
                  className="submit-button"
                  onClick={handleSubmit}
                  disabled={loadingSubmit || referral?.status === 'submitted' || duplicateDocumentNames.length > 0}
                  title={duplicateDocumentNames.length > 0 ? 'Cannot submit with duplicate document names' : ''}
                >
                  {loadingSubmit ? 'Submitting...' : 
                   referral?.status === 'failed' ? 'Retry Submission' : 'Submit to Accelerant'}
                </button>

                {submitError && (
                  <div className="submit-error-container">
                    <strong>Error:</strong> {submitError}
                  </div>
                )}

              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ApplicationForm; 